import Label from "../../../components/FormComponents/Label";
import Input from "../../../components/FormComponents/Input";
import ChipsInput from "../../../components/FormComponents/ChipsInput";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import Button from "../../../components/FormComponents/Button";
import { useState } from "react";
import HttpReqRateLimiter from "./HttpReqRateLimiter";
import HttpReqBodyDefinition from "./HttpReqBodyDefinition";
import HttpOutputVariableBinding from "./HttpOutputVariableBinding";
import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";
import Textarea from "../../../components/FormComponents/Textarea";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";

const httpMethods = ["GET", "POST", "PATCH", "PUT", "DELETE"];
const methodsWithoutBody = ["GET", "DELETE"];
const bodyOptions = [
  { id: "none", name: "No Body" },
  { id: "raw", name: "Raw Text" },
  { id: "raw-key-value", name: "Raw (Key-Value Pairs)" },
  { id: "form-data", name: "Form Data (Multipart)" },
  { id: "url-encoded", name: "URL Encoded (x-www-form-urlencoded)" }
];

const HttpReqConfiguration = ({ selectedApiData, setSelectedApiData }) => {
  const handleChange = (key, value) => {
    setSelectedApiData({
      ...selectedApiData,
      [key]: value
    });
  };
  return (
    <>
      <div className="mb_3 d_flex flex_column gap_2">
        <Label>Method</Label>
        <select
          className="builder_input"
          value={selectedApiData?.method || ""}
          onChange={(e) => handleChange("method", e.target.value)}
        >
          <option value="">Select HTTP method</option>
          {httpMethods.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
      </div>

      <div className="mb_3 d_flex flex_column gap_2">
        <Label>URL</Label>
        <Input type="text" value={selectedApiData?.url || ""} onChange={(e) => handleChange("url", e.target.value)} />
        <Label>Rate Limit</Label>
        <div className="border_full p_3">
          <HttpReqRateLimiter
            rateLimiter={selectedApiData?.rate_limit || {}}
            onChange={(data) => handleChange("rate_limit", data)}
          />
        </div>
      </div>
      <div className="mb_3 d_flex flex_column gap_2">
        <ChipsInput
          inputArray={selectedApiData?.query_params || []}
          onChange={(newChips) => handleChange("query_params", newChips)}
          label="Query Params"
          placeholder="Press Enter to add param"
        />
      </div>

      <div className="mb_3 d_flex flex_column gap_2">
        <Label>Header Parameters:</Label>
        {(selectedApiData?.header_parameters || []).length > 0 && (
          <div className="d_flex align_center gap_2 mb_1">
            <Label className="flex_1">Key</Label>
            <Label className="flex_1">Value</Label>
            <span style={{ width: "24px" }} />
          </div>
        )}
        {(selectedApiData?.header_parameters || []).map((header, index) => (
          <div key={index} className="d_flex align_center gap_2 mb_2">
            <Input
              type="text"
              placeholder="Key"
              value={header.key}
              onChange={(e) => {
                const newHeaders = [...(selectedApiData?.header_parameters || [])];
                newHeaders[index].key = e.target.value;
                handleChange("header_parameters", newHeaders);
              }}
            />
            <Input
              type="text"
              placeholder="Value"
              value={header.value}
              onChange={(e) => {
                const newHeaders = [...(selectedApiData?.header_parameters || [])];
                newHeaders[index].value = e.target.value;
                handleChange("header_parameters", newHeaders);
              }}
            />

            <MaterialIcon
              className="delete_icon"
              name={"delete"}
              onClick={() => {
                const newHeaders = [...(selectedApiData?.header_parameters || [])];
                newHeaders.splice(index, 1);
                handleChange("header_parameters", newHeaders);
              }}
            />
          </div>
        ))}
        <Button
          className="w_100 ml_auto"
          variant="secondary"
          onClick={() => {
            const newHeaders = [...(selectedApiData?.header_parameters || []), { key: "", value: "" }];
            handleChange("header_parameters", newHeaders);
          }}
        >
          + Add Header
        </Button>
      </div>

      {!methodsWithoutBody.includes(selectedApiData?.method) && (
        <div className="mb_3 d_flex flex_column gap_2">
          <Label>Body Option:</Label>
          <select
            className="builder_input"
            value={selectedApiData?.body_option || ""}
            onChange={(e) => handleChange("body_option", e.target.value)}
          >
            <option value="">Select body option</option>
            {bodyOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>

          <HttpReqBodyDefinition selectedApiData={selectedApiData} setSelectedApiData={setSelectedApiData} />
        </div>
      )}
      <div className="mb_3 d_flex flex_column gap_2">
        <HttpOutputVariableBinding
          variables={selectedApiData?.http_output_variable_bindings || []}
          onChange={(data) => handleChange("http_output_variable_bindings", data)}
        />
        <OutputTransformJs
          label="Output Transformation Function"
          value={selectedApiData?.output_transform_customjs}
          onChange={(v) => handleChange("output_transform_customjs", v)}
        />
        <OutputTransformJs
          label="Pre-process Transformation Function"
          value={selectedApiData?.pre_process_custom_js}
          onChange={(v) => handleChange("pre_process_custom_js", v)}
        />
        <HttpBlockOnFailure
          blockOnFailure={selectedApiData?.block_on_failure}
          blockOnFailureParameters={selectedApiData?.block_on_failure_handlers || []}
          onChangeParameters={(data) => handleChange("block_on_failure_handlers", data)}
          onChangeBlockOnFailure={(data) => handleChange("block_on_failure", data)}
        />
      </div>
    </>
  );
};
const OutputTransformJs = ({ value, onChange, label }) => {
  const [isExpanded, setExpanded] = useState(false);

  return (
    <>
      <div className={"d_flex justify_between"}>
        <Label>{label}</Label>
        <ButtonActionIcon type={"open_in_full"} onClick={() => setExpanded(true)} />
      </div>
      <Textarea
        value={value}
        onChange={({ target }) => onChange(target.value)}
        placeholder={"Enter Transformation Function"}
      />

      <BuilderModal isOpen={isExpanded} onClose={() => setExpanded(false)} modalName={"Transformation Function"}>
        <CodeEditor
          height="70vh"
          containerClass={"w_70_vw"}
          value={value}
          handleChange={onChange}
          enableBacktickTrigger={false}
        />
      </BuilderModal>
    </>
  );
};

const operatorOptions = [
  { id: "empty", name: "Is Empty" },
  { id: "equals_ignore_case", name: "Equals (Ignore Case)" },
  { id: "contains_str", name: "Contains" },
  { id: "contains_str_ignore_case", name: "Contains (Ignore Case)" },
  { id: "==", name: "Equals" }
];

const HttpBlockOnFailure = ({
  blockOnFailure,
  blockOnFailureParameters,
  onChangeParameters,
  onChangeBlockOnFailure
}) => {
  const handleToggle = (e) => {
    const isChecked = e.target.checked;
    onChangeBlockOnFailure(isChecked);
  };

  const handleParamChange = (index, key, value) => {
    const updatedParams = [...(blockOnFailureParameters || [])];
    updatedParams[index] = { ...updatedParams[index], [key]: value };
    onChangeParameters(updatedParams);
  };

  const addCondition = () => {
    const updatedParams = [...(blockOnFailureParameters || []), { path: "", operator: "", value: "" }];
    onChangeParameters(updatedParams);
  };

  const removeCondition = (index) => {
    const updatedParams = [...(blockOnFailureParameters || [])];
    updatedParams.splice(index, 1);
    onChangeParameters(updatedParams);
  };

  return (
    <div className="mb_3 d_flex flex_column gap_2">
      <Input label={"Block on Failure"} type="checkbox" checked={!!blockOnFailure} onChange={handleToggle} />

      {blockOnFailure && (
        <div className="d_flex flex_column gap_3 pl_2 border_left ml_1">
          {(blockOnFailureParameters || []).map((param, index) => (
            <div key={index} className="d_flex flex_column gap_2 border_full p_3 border_radius_4 bg_white relative">
              <div className="d_flex justify_between align_center mb_1">
                <Label className="text_bold">Condition {index + 1}</Label>
                <MaterialIcon
                  className="delete_icon cursor_pointer text_danger"
                  name={"delete"}
                  onClick={() => removeCondition(index)}
                />
              </div>

              <Input
                label={"JSON Path"}
                type="text"
                value={param.path || ""}
                onChange={(e) => handleParamChange(index, "path", e.target.value)}
                placeholder="e.g., data.status"
              />

              <div className="d_flex gap_2">
                <div className="d_flex flex_column gap_1 flex_1 w_50">
                  <Label>Operator</Label>
                  <select
                    className="builder_input"
                    value={param.operator || ""}
                    onChange={(e) => handleParamChange(index, "operator", e.target.value)}
                  >
                    <option value="">Select Operator</option>
                    {operatorOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
                {param.operator !== "empty" && (
                  <div className="flex_1 w_50">
                    <Input
                      label={"Expected Value"}
                      type="text"
                      value={param.value || ""}
                      onChange={(e) => handleParamChange(index, "value", e.target.value)}
                      placeholder="e.g., error"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
          <Button className="w_100" variant="secondary" onClick={addCondition}>
            + Add Condition
          </Button>
        </div>
      )}
    </div>
  );
};
export default HttpReqConfiguration;
